import { Building2, Target, BarChart3, Layout, Activity, Zap } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'Direction sits apart from delivery',
    description:
      'The corporate plan and team project lists can leave different views of what the organisation is pursuing.',
    icon: Building2,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Responsibility is hard to map',
    description:
      'A strategic priority needs a clear connection to the people and positions responsible for its work.',
    icon: Target,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Preparing the story takes effort',
    description:
      'Bringing company objectives, strategy choices, and work progress together can slow a leadership discussion.',
    icon: BarChart3,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'Company strategic foundation',
    description: 'Keep your vision, mission, values, and measurable objectives together.',
    icon: Building2,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Objectives connected to delivery',
    description: 'Trace strategic objectives through strategies, projects, tasks, and subtasks.',
    icon: Target,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Business assessment frameworks',
    description: 'Record PESTEL, Five Forces, SWOT, driving forces, and success factors.',
    icon: Layout,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Organisation and accountability',
    description:
      'Map divisions, teams, and positions and assign responsibility for projects and tasks.',
    icon: Activity,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Progress and reports',
    description: 'Explore work progress and prepare custom visual reports with PDF export.',
    icon: BarChart3,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'AI planning assistance',
    description:
      'Draft foundation statements, objectives, strategies, and report descriptions from your company context.',
    icon: Zap,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function CorporateStrategySoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Connect company direction to the people delivering it."
      subheadline="Bring your strategic foundation, business assessments, organisation structure, and connected projects into one place to plan and review the work ahead."
      bullets={[
        'Company direction and strategic objectives',
        'Divisions, teams, positions, and people',
        'Strategies connected to projects and tasks',
        'Visual reports for leadership discussions',
      ]}
      badgeText="Corporate Strategy"
      problemTitle="Company-wide direction needs visible responsibility."
      problemSubtitle="The strategic picture is easier to discuss when the priorities, organisation structure, and delivery plan can be considered together."
      problemCards={problemCards}
      solutionTitle="Bring the organisation and the strategy into one picture."
      solutionSubtitle="StratPlan combines company planning, a visual org chart, and a connected work hierarchy to make the route from direction to delivery visible."
      solutionSteps={[
        {
          title: 'Establish company direction',
          description:
            'Define your vision and measurable objectives, then assess external and internal factors that inform your strategy.',
        },
        {
          title: 'Map people and work',
          description:
            'Build divisions, teams, and positions. Connect strategies to projects and tasks and assign owners.',
        },
        {
          title: 'Present the strategic picture',
          description:
            'Review work progress in the hierarchy and create custom visual reports for leadership discussions.',
        },
      ]}
      featuresTitle="A clear structure for company-wide strategy"
      features={features}
      finalCtaTitle="Make company direction easier to carry into action."
      finalCtaSubtitle="Create your account and bring your organisation, strategic priorities, and delivery plan together."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="corporate_strategy_landing"
      breadcrumbLabel="Corporate Strategy"
    />
  );
}
